import type { DuvidaEadItem, DuvidaEadReacao, DuvidaEadUsuarioResposta } from "@/interfaces/ensino-ead.interface";

export function processarReacoesDuvida(reacoes: DuvidaEadReacao[] | undefined): Record<string, number> {
  const map: Record<string, number> = {};
  if (!Array.isArray(reacoes)) return map;
  for (const reacao of reacoes) {
    const emoji = reacao.TipoReacao;
    if (!emoji) continue;
    map[emoji] = (map[emoji] || 0) + 1;
  }
  return map;
}

export function processarJaReagiuDuvida(
  reacoes: DuvidaEadReacao[] | undefined,
  usuarioLogadoId?: string,
): string[] {
  if (!usuarioLogadoId || !Array.isArray(reacoes)) return [];
  const emojis = reacoes
    .filter((r) => r.UserId === usuarioLogadoId)
    .map((r) => r.TipoReacao)
    .filter(Boolean);
  return [...new Set(emojis)];
}

export function processarDuvidaEadItem(
  duvida: DuvidaEadItem,
  usuarioLogadoId?: string,
): DuvidaEadItem {
  const reacoesRaw = duvida.reacoes ?? [];
  return {
    ...duvida,
    Reacoes: processarReacoesDuvida(reacoesRaw),
    JaReagiu: processarJaReagiuDuvida(reacoesRaw, usuarioLogadoId),
    respostas: (duvida.respostas ?? []).map((r) => processarDuvidaEadItem(r, usuarioLogadoId)),
  };
}

export function autorComentarioDuvida(
  duvida: DuvidaEadItem,
): DuvidaEadUsuarioResposta | null {
  if (duvida.usuarioCadastro) return duvida.usuarioCadastro;
  if (duvida.ParentId != null) {
    return duvida.usuarioResposta ?? null;
  }
  return null;
}

export function fotoArquivoFromUsuario(
  usuario: DuvidaEadUsuarioResposta | null | undefined,
): string | undefined {
  if (!usuario) return undefined;

  const fotoEquipante = usuario.equipantes?.arquivos?.[0];
  if (fotoEquipante) {
    const ext = fotoEquipante.Extensao?.replace(/^\./, "") || "jpg";
    return `${fotoEquipante.Id}.${ext}`;
  }

  if (usuario.FotoId) {
    const ext = usuario.foto?.Extensao?.replace(/^\./, "") || "jpg";
    return `${usuario.FotoId}.${ext}`;
  }

  return undefined;
}

export interface AutorAvatarResolved {
  nome: string;
  email?: string;
  equipanteId?: number;
  fotoArquivo?: string;
}

export function resolveAutorAvatarDuvida(
  duvida: DuvidaEadItem,
  fallbackAluno: string,
  fallbackProfessor = "Professor",
): AutorAvatarResolved {
  const autor = duvida.autor;
  const usuario = autorComentarioDuvida(duvida);

  const nome =
    autor?.nome?.trim() || nomeAutorDuvida(duvida, fallbackAluno, fallbackProfessor);
  const email = autor?.email?.trim() || emailAutorDuvida(duvida);
  const equipanteId = autor?.equipanteId ?? equipanteIdAutorDuvida(duvida);

  const fotoArquivo =
    autor?.fotoArquivo?.trim() || fotoArquivoFromUsuario(usuario) || undefined;

  return {
    nome,
    email,
    equipanteId,
    fotoArquivo,
  };
}

export function fotoAutorDuvida(usuario: DuvidaEadUsuarioResposta | null | undefined): string | undefined {
  return fotoArquivoFromUsuario(usuario);
}

export function nomeAutorDuvida(
  duvida: DuvidaEadItem,
  fallbackAluno: string,
  fallbackProfessor = "Professor",
): string {
  if (duvida.autor?.nome?.trim()) return duvida.autor.nome.trim();

  const autor = autorComentarioDuvida(duvida);
  const nomeAutor =
    autor?.equipantes?.Nome?.trim() || autor?.UserName?.trim() || "";
  if (nomeAutor) return nomeAutor;
  if (duvida.ParentId == null && duvida.participante?.Nome?.trim()) {
    return duvida.participante.Nome.trim();
  }
  return duvida.ParentId != null ? fallbackProfessor : fallbackAluno;
}

export function emailAutorDuvida(duvida: DuvidaEadItem): string | undefined {
  if (duvida.autor?.email?.trim()) return duvida.autor.email.trim();

  const autor = autorComentarioDuvida(duvida);
  const email = autor?.equipantes?.Email?.trim();
  if (email) return email;
  const userName = autor?.UserName?.trim();
  if (userName?.includes("@")) return userName;
  if (duvida.ParentId == null) {
    return duvida.participante?.Email?.trim() || undefined;
  }
  return undefined;
}

export function equipanteIdAutorDuvida(duvida: DuvidaEadItem): number | undefined {
  if (duvida.autor?.equipanteId) return duvida.autor.equipanteId;

  const autor = autorComentarioDuvida(duvida);
  return autor?.equipantes?.Id ?? autor?.EquipanteId ?? undefined;
}

export function fotoAutorComentarioDuvida(duvida: DuvidaEadItem): string | undefined {
  return fotoAutorDuvida(autorComentarioDuvida(duvida));
}

export function podeResponderDuvida(
  duvida: DuvidaEadItem,
  opts: { modoProfessor: boolean; participanteId?: number },
): boolean {
  if (opts.modoProfessor) return true;
  if (opts.participanteId == null) return false;
  return duvida.ParticipanteId === opts.participanteId;
}

export function isDonoComentarioDuvida(
  duvida: DuvidaEadItem,
  usuarioLogadoId?: string,
): boolean {
  if (!usuarioLogadoId) return false;
  if (duvida.autor?.userId === usuarioLogadoId) return true;
  if (duvida.UsuarioCadastroId === usuarioLogadoId) return true;
  const autor = autorComentarioDuvida(duvida);
  return autor?.Id === usuarioLogadoId;
}
